import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import Icona from '../cliente/Icone';
import { messaggioErrore } from '../../utils/agenda';

// "Scrivi cosa vuoi fare" nel nuovo stile (Oggi, agenda, scheda cliente): il backend capisce la frase con le sue
// regole e con quello che ha imparato (backend/tuttofare.py, tuttofare_impara.py), qui si vede SEMPRE l'anteprima
// e si conferma. Nome ambiguo: si sceglie. Frase capita a metà: NON si indovina, si apre il mini-modulo
// Tipo · Chi/Cosa · Quando già precompilato (due tocchi e conferma). Quando la risposta viene dall'apprendimento
// l'anteprima lo dice ("imparato da un comando precedente").

const OGGI = () => new Date().toISOString().slice(0, 10);
// il mini-modulo sta dentro la stessa scatola dell'anteprima: etichetta sopra, campo sotto
const CAMPO = { display: 'flex', flexDirection: 'column', gap: '.2rem', fontSize: '.72rem', color: 'var(--mute)' };
const DENTRO = { background: 'rgba(0,0,0,.25)', border: '1px solid var(--line2)', borderRadius: '10px', padding: '.35rem .5rem', color: 'inherit', font: 'inherit' };
const RIGA = { display: 'flex', flexWrap: 'wrap', gap: '.6rem', alignItems: 'flex-end', width: '100%' };

// Quanto della frase il server legge davvero: `tuttofare.interpreta(testo[:500], ctx)` (backend/rotte_tuttofare.py).
// La casella non aveva nessun limite: una nota lunga incollata (667 caratteri) veniva letta a metà e la nota finiva
// in scheda mozzata a metà parola, senza un avviso. Stessa regola della ricerca (ottavo giro 6) e dei campi nota
// (nono giro B3): il limite vale anche per l'incollato, che passa sopra `maxLength`.
export const MAX_TESTO = 500;

// Con il filtro persona acceso ("/admin?persona=u-karolina") l'attività va nell'agenda della persona che si sta
// guardando, non nella mia: prima nasceva assegnata a chi scriveva, il messaggio verde diceva "In agenda oggi alle
// 23: …" e la giornata mostrata non cambiava di una riga. `persona` è l'id di chi si guarda (niente quando si guarda
// la propria giornata o "Tutte"), e viaggia con la frase in tutte e due le chiamate: l'anteprima dice dove va, e
// `esegui` ci scrive davvero.
const Tuttofare = ({ onFatto, etichetta = 'Tuttofare', placeholder, persona = null, nomePersona = '' }) => {
  const [testo, setTesto] = useState('');
  const [risposta, setRisposta] = useState(null);
  const [modulo, setModulo] = useState(null);
  const [cerca, setCerca] = useState('');
  const [trovati, setTrovati] = useState([]);
  const [attesa, setAttesa] = useState(false);
  const [corretto, setCorretto] = useState(false);  // la frase è passata dal mini-modulo: c'è da imparare

  // Le risposte vecchie si buttano (stessa regola della ricerca globale, secondo giro A2): con `interpreta` lento
  // (una rete da telefono) si scriveva "richiama Olena domani alle 10", Vai, poi si riscriveva tutt'altro —
  // l'anteprima si svuotava, com'è giusto — e la risposta vecchia la faceva ricomparire sopra una casella che
  // ormai diceva un'altra cosa, con Conferma pronto a fare la telefonata di prima. `domanda` è il numero della
  // richiesta in corso, `scartaFinoA` quelle che non valgono più (frase cambiata, Annulla, Conferma andato a buon
  // fine): vale solo la risposta all'ultima domanda fatta, e solo se nessuno l'ha scartata nel frattempo.
  const domanda = useRef(0);
  const scartaFinoA = useRef(0);
  const dimentica = () => { scartaFinoA.current = domanda.current; };
  const pulisci = () => { setRisposta(null); setModulo(null); setCerca(''); setTrovati([]); setCorretto(false); dimentica(); };
  const scrivi = (valore) => { setTesto(valore.slice(0, MAX_TESTO)); pulisci(); };
  const chiudi = pulisci;

  const interpreta = async (corpo) => {
    domanda.current += 1;
    const mia = domanda.current;
    const vale = () => mia === domanda.current && mia > scartaFinoA.current;
    setAttesa(true);
    try {
      const r = await axios.post(`${API}/admin/tuttofare/interpreta`, { ...corpo, persona: persona || undefined });
      if (vale()) {
        setRisposta(r.data);
        setModulo(r.data.modulo ? { ...r.data.modulo, tipo: r.data.modulo.tipo || '' } : null);
        if (corpo.modulo) setCorretto(true);
      }
    } catch (e) {
      // un "persona" che il server non prende (400/403) lo dice lui: prima si leggeva sempre la stessa frase nostra
      if (vale()) toast.error(messaggioErrore(e, 'Non riesco a leggere la frase ora'));
    } finally {
      // l'attesa la spegne solo la domanda in corso: una risposta vecchia non riaccende il "Vai" di quella nuova
      if (mia === domanda.current) setAttesa(false);
    }
  };
  const conferma = async () => {
    setAttesa(true);
    try {
      const r = await axios.post(`${API}/admin/tuttofare/esegui`, {
        azione: risposta.azione, parametri: risposta.parametri, testo, corretto, persona: persona || undefined,
      });
      toast.success(r.data.fatto);
      setTesto('');
      pulisci();
      if (onFatto) onFatto();
    } catch (e) {
      toast.error(messaggioErrore(e));
    } finally { setAttesa(false); }
  };

  // il "Chi/Cosa" del mini-modulo: si cerca fra clienti, agenzie e immobili
  const cercaChi = useCallback(async (q) => {
    if (!q || q.trim().length < 2) { setTrovati([]); return; }
    try { setTrovati((await axios.get(`${API}/admin/tuttofare/chi`, { params: { q } })).data.voci || []); } catch { setTrovati([]); }
  }, []);
  useEffect(() => {
    if (!modulo) return undefined;
    const t = setTimeout(() => cercaChi(cerca), 250);
    return () => clearTimeout(t);
  }, [cerca, modulo, cercaChi]);

  const campo = (k) => (e) => setModulo({ ...modulo, [k]: e.target.value });

  return (
    <div data-testid="tuttofare">
      <form className="quick r" onSubmit={(e) => { e.preventDefault(); if (testo.trim()) interpreta({ testo }); }}>
        {etichetta ? <span className="k">{etichetta}</span> : <Icona nome="piu" />}
        <input value={testo} maxLength={MAX_TESTO} onChange={(e) => scrivi(e.target.value)} aria-label="Scrivi cosa vuoi fare"
          placeholder={placeholder || 'Scrivi cosa vuoi fare · “richiamare Zetaci domani alle 10” · “referente di Tecnocasa Cormano: Matteo 333…”'} />
        <button type="submit" className="btn p sm" disabled={attesa || !testo.trim()}>Vai</button>
      </form>
      {risposta?.anteprima && (
        <div className="quick-risposta" role="status">
          <p>
            {risposta.anteprima}
            {risposta.imparato && <span className="tag t-b" style={{ marginLeft: '.4rem' }}>{risposta.imparato}</span>}
          </p>
          {/* si sta guardando la giornata di un altro: l'anteprima dice in che agenda finisce. Se il server lo
              scrive già dentro l'anteprima non si ripete */}
          {persona && nomePersona && !String(risposta.anteprima).includes(nomePersona) && (
            <span className="muto" data-testid="tuttofare-persona">Va nell'agenda di {nomePersona}.</span>
          )}
          <button type="button" className="btn p sm" disabled={attesa} onClick={conferma}>Conferma</button>
          <button type="button" className="btn sm" onClick={chiudi}>Annulla</button>
        </div>
      )}
      {risposta?.domanda && (
        <div className="quick-risposta">
          <p>{risposta.domanda}</p>
          <div className="sugg">
            {risposta.scelte.map((s) => (
              <button key={`${s.tipo}-${s.id}`} type="button" disabled={attesa}
                onClick={() => interpreta({ precedente: risposta, scelta: { tipo: s.tipo, id: s.id } })}>{s.etichetta}</button>
            ))}
            <button type="button" onClick={chiudi}>Annulla</button>
          </div>
        </div>
      )}
      {modulo && (
        <div className="quick-risposta" data-testid="tuttofare-modulo">
          <p>{risposta?.messaggio || 'Non ho capito tutto: controllate qui sotto e confermate.'}</p>
          <div style={RIGA}>
            <label style={CAMPO}>Tipo
              <select aria-label="Tipo" style={DENTRO} value={modulo.tipo || ''} onChange={campo('tipo')}>
                <option value="">…</option>
                {(modulo.tipi || []).map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
              </select>
            </label>
            <div style={{ ...CAMPO, flex: 1, minWidth: '160px' }}>
              <span>Chi</span>
              {modulo.chi
                ? <button type="button" className="chip on" style={{ ...DENTRO, cursor: 'pointer' }} onClick={() => setModulo({ ...modulo, chi: null })}>{modulo.chi.etichetta} ✕</button>
                : <input aria-label="Chi" style={DENTRO} placeholder="cerca il cliente o l'agenzia" value={cerca} onChange={(e) => setCerca(e.target.value)} />}
            </div>
            <label style={CAMPO}>Quando
              <input type="date" aria-label="Quando" style={DENTRO} value={modulo.data || OGGI()} onChange={campo('data')} />
            </label>
            <label style={CAMPO}>Ora
              <input type="time" aria-label="A che ora" style={DENTRO} value={modulo.ora || ''} onChange={campo('ora')} />
            </label>
          </div>
          <div style={RIGA}>
            <label style={{ ...CAMPO, flex: 1, minWidth: '220px' }}>Cosa
              <input aria-label="Cosa" style={DENTRO} value={modulo.cosa || ''} onChange={campo('cosa')} />
            </label>
          </div>
          {!modulo.chi && (
            <div className="sugg">
              {(trovati.length ? trovati : modulo.candidati || []).map((c) => (
                <button key={`${c.tipo}-${c.id}`} type="button"
                  onClick={() => setModulo({ ...modulo, chi: { tipo: c.tipo, id: c.id, etichetta: c.etichetta } })}>{c.etichetta}</button>
              ))}
            </div>
          )}
          <div className="sugg">
            <button type="button" className="btn p sm" disabled={attesa || !modulo.tipo} onClick={() => interpreta({ modulo })}>Vedi cosa faccio</button>
            <button type="button" className="btn sm" onClick={pulisci}>Annulla</button>
          </div>
        </div>
      )}
      {risposta && !risposta.capito && !modulo && (
        <div className="quick-risposta no">
          <p>{risposta.messaggio}</p>
          <div className="sugg">
            {(risposta.suggerimenti || []).map((s) => (
              <button key={s} type="button" onClick={() => scrivi(s)}>{s}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Tuttofare;
